/**
 * useInvoiceActions — all action handlers: post, void, write-off, PDF, submit.
 */

import { useState, useCallback } from 'react'

import type { Party } from '@/gen/proto'

import { todayISODate, buildCloneSource } from './invoices-helpers'
import {
  submitExistingInvoice, submitNewInvoice,
  useInvoicePaymentsState, useWriteOffActions,
} from './useInvoiceActions-helpers'
import { useToast } from '../../components/ui'
import {
  postInvoice as postInvoiceApi,
  voidInvoice as voidInvoiceApi,
  downloadInvoicePDF,
} from '../../services'
import { type InvoicePaymentsResponse } from '../../services/payment-service'

import type { Invoice, InvoiceFormState } from '../../types/billing'

export interface InvoiceActionsProps {
  organizationId: string
  currentLedgerId: string | null
  isVendor: boolean
  selectedInvoice: Invoice | null
  setSelectedInvoice: (inv: Invoice | null) => void
  setIsLoading: (v: boolean) => void
  fetchInvoices: (token?: string) => Promise<void>
  // Mirrors react-router's navigate; the optional state carries the clone seed.
  navigate: (path: string, options?: { state?: unknown }) => void
  basePath: string
  viewMode: string
  parties?: Party[]
}

export interface InvoiceActionsState {
  // Payment panel
  invoicePayments: InvoicePaymentsResponse | null
  isInvoicePaymentsLoading: boolean
  fetchInvoicePayments: (invoiceId: string) => Promise<void>

  // Write-off
  isWriteOffSaving: boolean
  isWriteOffConfirmVisible: boolean
  setIsWriteOffConfirmVisible: (v: boolean) => void
  writeOffType: 'rounding' | 'bad_debt'
  setWriteOffType: (t: 'rounding' | 'bad_debt') => void
  writeOffNote: string
  setWriteOffNote: (n: string) => void
  handleWriteOff: () => Promise<void>
  handleReverseWriteOff: () => Promise<void>
  confirmingAction: 'rounding' | 'bad_debt' | null
  setConfirmingAction: (a: 'rounding' | 'bad_debt' | null) => void
  confirmTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>

  // Version history / payment modal
  isVersionHistoryVisible: boolean
  setIsVersionHistoryVisible: (v: boolean) => void
  isPaymentModalVisible: boolean
  setIsPaymentModalVisible: (v: boolean) => void

  // Post confirmation
  isPostConfirmVisible: boolean
  setIsPostConfirmVisible: (v: boolean) => void
  invoiceToPost: Invoice | null
  postingDate: string
  setPostingDate: (d: string) => void
  handlePost: (invoice: Invoice) => void
  confirmPost: () => Promise<void>

  // Void confirmation
  isVoidModalVisible: boolean
  setIsVoidModalVisible: (v: boolean) => void
  invoiceToVoid: Invoice | null
  voidReason: string
  setVoidReason: (r: string) => void
  handleVoid: (invoice: Invoice) => void
  confirmVoid: () => Promise<void>

  // Form submit
  handleSubmit: (formState: InvoiceFormState) => Promise<void>

  // PDF
  handleGeneratePDF: (invoice: Invoice) => Promise<void>

  // Navigation
  handleCreate: () => void
  handleView: (invoice: Invoice) => void
  handleEdit: (invoice: Invoice) => void
  handleClone: (invoice: Invoice) => void
  handleBack: () => void
  handleLoadMore: () => void
  nextPageToken: string | undefined
}

interface PostState {
  isPostConfirmVisible: boolean
  setIsPostConfirmVisible: (v: boolean) => void
  invoiceToPost: Invoice | null
  postingDate: string
  setPostingDate: (d: string) => void
  handlePost: (invoice: Invoice) => void
  confirmPost: () => Promise<void>
}

interface UsePostArgs {
  props: InvoiceActionsProps
  addToast: ReturnType<typeof useToast>['addToast']
}

function usePostActions({ props, addToast }: UsePostArgs): PostState {
  const { organizationId, isVendor, selectedInvoice, setSelectedInvoice, setIsLoading, fetchInvoices, viewMode } = props
  const [isPostConfirmVisible, setIsPostConfirmVisible] = useState(false)
  const [invoiceToPost, setInvoiceToPost] = useState<Invoice | null>(null)
  const [postingDate, setPostingDate] = useState('')

  const handlePost = useCallback((invoice: Invoice) => {
    setInvoiceToPost(invoice)
    setPostingDate(todayISODate())
    setIsPostConfirmVisible(true)
  }, [])

  const confirmPost = useCallback(async () => {
    if (!invoiceToPost) { return }
    setIsPostConfirmVisible(false)
    setIsLoading(true)
    try {
      const response = await postInvoiceApi(organizationId, invoiceToPost.id, postingDate || undefined)
      if (response.data) {
        addToast({ variant: 'success', message: `Invoice ${invoiceToPost.invoiceNumber} has been posted` })
        await fetchInvoices()
        if (viewMode === 'view' && selectedInvoice?.id === invoiceToPost.id) {
          setSelectedInvoice(response.data)
        }
      } else if (response.error) {
        throw new Error(response.error.message)
      }
    } catch (error) {
      addToast({
        variant: 'error',
        message: error instanceof Error ? error.message : `Failed to post ${isVendor ? 'bill' : 'invoice'}. Please try again.`,
      })
    } finally {
      setIsLoading(false)
      setInvoiceToPost(null)
    }
  }, [invoiceToPost, postingDate, addToast, fetchInvoices, viewMode, selectedInvoice, organizationId, isVendor, setIsLoading, setSelectedInvoice])

  return { isPostConfirmVisible, setIsPostConfirmVisible, invoiceToPost, postingDate, setPostingDate, handlePost, confirmPost }
}

interface VoidState {
  isVoidModalVisible: boolean
  setIsVoidModalVisible: (v: boolean) => void
  invoiceToVoid: Invoice | null
  voidReason: string
  setVoidReason: (r: string) => void
  handleVoid: (invoice: Invoice) => void
  confirmVoid: () => Promise<void>
}

interface RunVoidArgs {
  invoiceToVoid: Invoice
  voidReason: string
  organizationId: string
  isVendor: boolean
  selectedInvoice: Invoice | null
  setSelectedInvoice: (i: Invoice | null) => void
  fetchInvoices: () => Promise<void>
  viewMode: string
  addToast: ReturnType<typeof useToast>['addToast']
}

async function runVoid(a: RunVoidArgs): Promise<void> {
  try {
    const response = await voidInvoiceApi(a.organizationId, a.invoiceToVoid.id, a.voidReason)
    if (response.data) {
      a.addToast({ variant: 'success', message: `${a.isVendor ? 'Bill' : 'Invoice'} ${a.invoiceToVoid.invoiceNumber} has been voided` })
      await a.fetchInvoices()
      if (a.viewMode === 'view' && a.selectedInvoice?.id === a.invoiceToVoid.id) {
        a.setSelectedInvoice(response.data)
      }
    } else if (response.error) {
      throw new Error(response.error.message)
    }
  } catch (error) {
    a.addToast({
      variant: 'error',
      message: error instanceof Error ? error.message : `Failed to void ${a.isVendor ? 'bill' : 'invoice'}. Please try again.`,
    })
  }
}

function useVoidActions({ props, addToast }: UsePostArgs): VoidState {
  const { organizationId, isVendor, selectedInvoice, setSelectedInvoice, setIsLoading, fetchInvoices, viewMode } = props
  const [isVoidModalVisible, setIsVoidModalVisible] = useState(false)
  const [invoiceToVoid, setInvoiceToVoid] = useState<Invoice | null>(null)
  const [voidReason, setVoidReason] = useState('')

  const handleVoid = useCallback((invoice: Invoice) => {
    setInvoiceToVoid(invoice)
    setVoidReason('')
    setIsVoidModalVisible(true)
  }, [])

  const confirmVoid = useCallback(async () => {
    if (!invoiceToVoid || !voidReason.trim()) {
      addToast({ variant: 'error', message: 'Please provide a reason for voiding' })
      return
    }
    setIsVoidModalVisible(false)
    setIsLoading(true)
    await runVoid({
      invoiceToVoid, voidReason, organizationId, isVendor,
      selectedInvoice, setSelectedInvoice, fetchInvoices, viewMode, addToast,
    })
    setIsLoading(false)
    setInvoiceToVoid(null)
    setVoidReason('')
  }, [invoiceToVoid, voidReason, addToast, fetchInvoices, viewMode, selectedInvoice, organizationId, isVendor, setIsLoading, setSelectedInvoice])

  return { isVoidModalVisible, setIsVoidModalVisible, invoiceToVoid, voidReason, setVoidReason, handleVoid, confirmVoid }
}

interface NavState {
  handleCreate: () => void
  handleView: (invoice: Invoice) => void
  handleEdit: (invoice: Invoice) => void
  handleClone: (invoice: Invoice) => void
  handleBack: () => void
  handleLoadMore: () => void
  nextPageToken: string | undefined
}

function useNavigationActions(props: InvoiceActionsProps): NavState {
  const { navigate, basePath, fetchInvoices } = props
  const [nextPageToken] = useState<string | undefined>()
  const handleCreate = useCallback(() => { navigate(`${basePath}/new`) }, [navigate, basePath])
  const handleView = useCallback((invoice: Invoice) => { navigate(`${basePath}/${invoice.id}`) }, [navigate, basePath])
  const handleEdit = useCallback((invoice: Invoice) => { navigate(`${basePath}/${invoice.id}/edit`) }, [navigate, basePath])
  // Clone: open the create form pre-seeded with a copy of the source invoice.
  const handleClone = useCallback((invoice: Invoice) => {
    navigate(`${basePath}/new`, { state: { cloneSource: buildCloneSource(invoice) } })
  }, [navigate, basePath])
  const handleBack = useCallback(() => { navigate(basePath) }, [navigate, basePath])
  const handleLoadMore = useCallback(() => {
    if (nextPageToken) { void fetchInvoices(nextPageToken) }
  }, [nextPageToken, fetchInvoices])
  return { handleCreate, handleView, handleEdit, handleClone, handleBack, handleLoadMore, nextPageToken }
}

interface UseSubmitAndPdfArgs {
  props: InvoiceActionsProps
  addToast: ReturnType<typeof useToast>['addToast']
}

function useGeneratePdf({ props, addToast }: UseSubmitAndPdfArgs): (invoice: Invoice) => Promise<void> {
  const { organizationId, setIsLoading } = props
  return useCallback(async (invoice: Invoice) => {
    setIsLoading(true)
    try {
      const isSuccess = await downloadInvoicePDF(organizationId, invoice.id, invoice.invoiceNumber)
      if (isSuccess) {
        addToast({ variant: 'success', message: 'PDF downloaded successfully' })
      } else {
        throw new Error('Failed to generate PDF')
      }
    } catch {
      addToast({ variant: 'error', message: 'Failed to generate PDF. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }, [addToast, organizationId, setIsLoading])
}

function useSubmitInvoice({ props, addToast }: UseSubmitAndPdfArgs): (formState: InvoiceFormState) => Promise<void> {
  const { selectedInvoice, organizationId, currentLedgerId, fetchInvoices, navigate, basePath, setIsLoading } = props
  return useCallback(async (formState: InvoiceFormState) => {
    setIsLoading(true)
    try {
      if (selectedInvoice) {
        await submitExistingInvoice({ formState, selectedInvoice, organizationId, addToast, fetchInvoices, navigate, basePath })
      } else {
        await submitNewInvoice({ formState, organizationId, currentLedgerId, addToast, fetchInvoices, navigate, basePath })
      }
    } catch (error) {
      addToast({
        variant: 'error',
        message: error instanceof Error ? error.message : 'Failed to save invoice. Please try again.',
      })
    } finally {
      setIsLoading(false)
    }
  }, [selectedInvoice, addToast, fetchInvoices, navigate, organizationId, currentLedgerId, basePath, setIsLoading])
}

export function useInvoiceActions(props: InvoiceActionsProps): InvoiceActionsState {
  const { organizationId, selectedInvoice, setSelectedInvoice } = props
  const { addToast } = useToast()
  const payments = useInvoicePaymentsState(organizationId)
  const writeOff = useWriteOffActions({
    selectedInvoice, organizationId, setSelectedInvoice,
    fetchInvoicePayments: payments.fetchInvoicePayments, addToast,
  })
  const post = usePostActions({ props, addToast })
  const voidActions = useVoidActions({ props, addToast })
  const nav = useNavigationActions(props)
  const [isVersionHistoryVisible, setIsVersionHistoryVisible] = useState(false)
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false)
  const handleGeneratePDF = useGeneratePdf({ props, addToast })
  const handleSubmit = useSubmitInvoice({ props, addToast })

  return {
    ...payments, ...writeOff, ...post, ...voidActions, ...nav,
    isVersionHistoryVisible, setIsVersionHistoryVisible,
    isPaymentModalVisible, setIsPaymentModalVisible,
    handleSubmit, handleGeneratePDF,
  }
}
